import React, { useEffect } from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TakeFiveDashboardQueryModel, TakeFiveRiskEnum, TaskTakeFiveModel } from "../../../shared/models/api/models";
import { formatDateTime, toISOLocalDate } from "../../../shared/models/mapping/mapping";
import { getSubmittedTakeFives } from "../../../shared/services/railOperationalFormsApi";
import DateRangePicker from "../reporting/DateRangePicker";
import { TakeFiveView } from "./TakeFiveView";
import {
    ChevronRight as LinkIcon,
    ArrowDownward as SortDescIcon,
    ArrowUpward as SortAscIcon,
    ImportExport as SortNoneIcon,
    FiberManualRecord,
} from '@mui/icons-material';
import { overview, takeFiveOverviewPathRoute, takeFivePathRoute, takeFiveRoute } from "./TakeFiveRoutes";
import { Toggle } from "../../ui/ToggleContent";
import { PageList } from "../../ui/PageList";

interface AnswerProps {
    onAnswerUpdate: (filter: FilterItem) => any;
}
interface FilterItem {
    type: string,
    label: string,
    value: any,
}
interface FilterProps extends AnswerProps {
    onDelete: (index: number) => any,
    filters: FilterItem[],
    showSupervisor: boolean,
    visible?: boolean
}

interface DateRange {
    dateFrom: any,
    dateTo: any
}

class FilterType {
    static supervisor: string = 'Supervisor';
    static location: string = 'Location';
    static dateRange: string = 'Dates';
}

const FilterContainer = (props: FilterProps) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const [takeFiveDateFrom, setTakeFiveDateFrom] = useState(yesterday as Date | null);
    const [takeFiveDateTo, setTakeFiveDateTo] = useState(today as Date | null);

    const setTakeFiveDateRange = (dateFrom: Date | null, dateTo: Date | null): void => {
        setTakeFiveDateFrom(dateFrom);
        setTakeFiveDateTo(dateTo);       
    };

    useEffect(() => {
        let dateRangeFilter = {
            type: FilterType.dateRange,
            label: '',
            value: {
                dateFrom: takeFiveDateFrom,
                dateTo: takeFiveDateTo
            }
        }

        props.onAnswerUpdate(dateRangeFilter)
    }, [takeFiveDateFrom, takeFiveDateTo]);

    return (

        <div className="tf-list-filter">
            <div className="tf-list-filter-title">Select date range</div>
            <div className="tf-list-filter-body">
                <DateRangePicker
                    className="date-range-picker"
                    fromDate={takeFiveDateFrom ?? today}
                    toDate={takeFiveDateTo ?? yesterday}
                    setter={(f, t) => {
                        setTakeFiveDateRange(f ? f : null, t ? t : null);
                    }}
                />
            </div>
        </div>
    );
}

interface TakeFiveOverviewProps {
    view: TakeFiveView[],
    title: string,
    titleIcon?: any,
    showPageSize?: boolean,
    showHeaderLink?: boolean,
    showFilters?: boolean,
    showFooter?: boolean
}

export const TakeFiveOverview = (props: TakeFiveOverviewProps) => {
    const [loading, setLoading] = useState(false);
    const [currentView, setCurrentView] = useState(props.view[0]);
    const dateTimeFormat = new Intl.DateTimeFormat("en-AU", {});
    const [loadBackground, setLoadBackground] = useState(false);
    const [takeFives, setTakeFives] = useState([] as TaskTakeFiveModel[]);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [orderByOption, setOrderByOption] = useState(OrderByOption.takeFiveDateAsc);
    const [headerLink, setHeaderLink] = useState(undefined as string | undefined);

    const getDefaultFilters = () => {
        let defaultFilters = [] as FilterItem[];
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const previousDay = new Date(todayDate);
        previousDay.setDate(previousDay.getDate() - 1);
        defaultFilters.push(
            {
                type: FilterType.dateRange,
                label: dateTimeFormat.format(todayDate),
                value: {
                    dateFrom: previousDay,
                    dateTo: todayDate
                } as DateRange
            } as FilterItem
        );
        return defaultFilters;
    }
    const [filters, setFilters] = useState(getDefaultFilters());

    const handleFilterChange = (filter: FilterItem) => {
        if (filters.some(f => f.type === filter.type && f.value === filter.value)) {
            return; //prevent duplicates
        }

        let newFilters = [...filters];

        // Maximum of 1 date filter, replace old value if found
        if (filter.type === FilterType.dateRange) {
            let oldDateFilterIndex = filters.findIndex(f => { return f.type === FilterType.dateRange });
            if (oldDateFilterIndex > -1) {
                newFilters.splice(oldDateFilterIndex, 1);
            }
        }

        setFilters([...newFilters, filter]);
        setLoadBackground(true);
    }

    const onDeleteFilter = (index: number) => {
        let newFilters = [...filters];
        newFilters.splice(index, 1);
        setFilters(newFilters);
        setLoadBackground(true);
    }

    const getViewData = async () => {
        let items: TaskTakeFiveModel[] = [];

        if (currentView === TakeFiveView.Overview) {
            dashboardQuery.pending = true;
            items = await getSubmittedTakeFives(dashboardQuery);
            return items;
        }
    }

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            setLoading(true);
            const items = await getViewData();
            configHeaderLink();

            if (mounted && Array.isArray(items)) {
                setTakeFives(items);
                setLoading(false);
            }
        };

        mount();

        return () => { mounted = false; };
    }, [filters]);

    let dashboardQuery = { pending: false } as TakeFiveDashboardQueryModel;

    let dateFilter = filters.find(f => f.type === FilterType.dateRange);
    if (dateFilter !== undefined) {
        dashboardQuery.dateRange = dateFilter.value;
    }

    const orderByName = () => {
        let data: TaskTakeFiveModel[] = takeFives ? [...takeFives] : [];

        data.sort((a, b) => {
            let aName = a.createdBy || "";
            let bName = b.createdBy || "";
            return aName.localeCompare(bName);
        }
        );

        let sortBy = OrderByOption.NameAsc;
        if (orderByOption == OrderByOption.NameAsc) {
            sortBy = OrderByOption.NameDesc;
            data.reverse();
        }
        setOrderByOption(sortBy);
        setTakeFives(data);
    }

    const orderByTakeFiveDate = () => {
        let data: TaskTakeFiveModel[] = takeFives ? [...takeFives] : [];

        data.sort((a, b) => {
            let aTime = a.dateCreated || Date.now;
            let bTime = b.dateCreated || Date.now;
            if (aTime < bTime) return -1;
            if (aTime > bTime) return 1;
            return 0;
        }
        );

        let sortBy = OrderByOption.takeFiveDateAsc;
        if (orderByOption == OrderByOption.takeFiveDateAsc) {
            sortBy = OrderByOption.takeFiveDateDesc;
            data.reverse();
        }

        setOrderByOption(sortBy);
        setTakeFives(data);
    }

    const configHeaderLink = () => {
        if (!props.showHeaderLink) {
            return;
        }

        let link = undefined as string | string[] | undefined

        if (currentView === TakeFiveView.Overview) {
            link = takeFiveOverviewPathRoute.path;
        }
        else if (currentView === TakeFiveView.TakeFive) {
            link = takeFivePathRoute.path;
        }

        if (typeof (link) === 'string') {
            setHeaderLink(link);
        }

    }

    const moveToPage = (page: number) => {
        setPageNumber(page);
    }

    return (

        <>
            <ResultCountContainer
                viewCount={takeFives?.length}
                resultCount={takeFives?.length}
                pageSize={pageSize}
                setPageSize={setPageSize}
                link={headerLink}
                visible={true}
            />

            <FilterContainer
                filters={filters}
                showSupervisor={false}
                onAnswerUpdate={handleFilterChange}
                visible={true} //{props.showFilters}
                onDelete={onDeleteFilter}
            />

            <TfListContainer
                loading={loading}
                takeFives={takeFives}
                currentView={currentView}
                pageNumber={pageNumber}
                pageSize={pageSize}
                orderByOption={orderByOption}
                orderByName={orderByName}
                orderByTakeFiveDate={orderByTakeFiveDate}
            />

            <FooterContainer
                visible={true}
                pageNumber={pageNumber}
                pageSize={pageSize}
                totalResults={takeFives?.length}
                moveToPage={moveToPage}
            />
        </>
    );
};

enum OrderByOption {
    NameAsc,
    NameDesc,
    takeFiveDateAsc,
    takeFiveDateDesc
}

interface TfListProps {
    loading: boolean,
    adminView?: boolean,
    currentView: TakeFiveView,
    takeFives: TaskTakeFiveModel[] | undefined,
    pageNumber: number,
    pageSize: number,
    orderByOption: OrderByOption,
    orderByName: any,
    orderByTakeFiveDate: any
}

const TfListContainer = (props: TfListProps) => {
    const [displayTakeFives, setDisplayTakeFives] = useState(props.takeFives?.slice(0, props.pageSize));
    const navigate = useNavigate();

    const nameOrderIcon = () => {
        if (props.orderByOption === OrderByOption.NameAsc) {
            return (<SortDescIcon />);
        }
        else if (props.orderByOption === OrderByOption.NameDesc) {
            return (<SortAscIcon />);
        }
        return (<SortNoneIcon />);
    }

    const takeFiveDateOrderIcon = () => {
        if (props.orderByOption === OrderByOption.takeFiveDateAsc) {
            return (<SortDescIcon />);
        }
        else if (props.orderByOption === OrderByOption.takeFiveDateDesc) {
            return (<SortAscIcon />);
        }
        return (<SortNoneIcon />);
    }

    useEffect(() => {
        let start = (props.pageNumber - 1) * props.pageSize;

        let newDisplayTakeFives = props.takeFives?.slice(start, start + props.pageSize);

        setDisplayTakeFives(newDisplayTakeFives);

    }, [props.takeFives, props.pageNumber, props.pageSize]);

    const takeFiveDateFormatted = (date: any) => {
        var date = date || new Date();
        return formatDateTime(date);
    }

    const pages: { [id: number]: string; } = {};
    pages[TakeFiveView.Overview] = overview;

    const getActionPath = (id: number, tab: string) => {
        const url = `${takeFiveRoute.path}/${pages[props.currentView]}/${id}/${tab}`;
        return url;
    }

    const displayTakeFiveDetails = (event: any, takeFiveId?: number, tab?: string) => {
        event.stopPropagation();
        tab = 'details';

        const path = getActionPath(takeFiveId!, tab);
        navigate(path);
    }

    return (
        <>
            <div className="row takefive-card-row">
                <div className="col">
                    <div className="tf-col-location d-none d-xl-inline-block font-weight-bold">Task</div>
                </div>
                <div className="col">
                    <div className="tf-col-location d-none d-xl-inline-block font-weight-bold" onClick={props.orderByName}>Name {nameOrderIcon()}</div>
                </div>
                <div className="col">
                    <div className="tf-col-location d-none d-xl-inline-block font-weight-bold">Location</div>
                </div>
                <div className="col">
                    <div className="tf-col-location d-none d-xl-inline-block font-weight-bold" onClick={props.orderByTakeFiveDate}>Date {takeFiveDateOrderIcon()}</div>
                </div>
                <div className="col">
                    <div className="tf-col-location d-none d-xl-inline-block font-weight-bold">Risk</div>
                </div>               
            </div>

            <div className="take-five-divider row"></div>

            {(displayTakeFives !== undefined && displayTakeFives.length > 0) ? displayTakeFives.map((takeFive) => (
                <div className="row align-items-center takefive-card takefive-card-row" key={takeFive.id} onClick={(event) => displayTakeFiveDetails(event, takeFive.id)} style={{ cursor: 'pointer' }}>
                    <div className="col">
                        <div className="tf-col-location d-none d-xl-inline-block">{takeFive.taskName}</div>
                    </div>
                    <div className="col">
                        <div className="font-weight-bold">{takeFive.createdBy}</div>
                    </div>
                    <div className="col">
                        <div className="tf-col-location d-none d-xl-inline-block">{takeFive.location}</div>
                    </div>
                    <div className="col">
                        <div className="tf-col-location d-none d-xl-inline-block">{takeFiveDateFormatted(takeFive.dateCreated)}</div>
                    </div>
                    <div className="col">
                        <div className="tf-col-location d-none d-xl-inline-block">{displayRisk(takeFive.riskLevelEqualOrGreaterThanH11!)}</div>
                    </div>
                </div>
            ))
                :
                <div className="p-4 text-center">There are no actionable fatigue assessments to display.</div>
            }
        </>
    );
}

const displayRisk = (risk: boolean) => {
    const riskLevel = risk ? TakeFiveRiskEnum.High : TakeFiveRiskEnum.Low;
    if (riskLevel === undefined) {
        return (<></>);
    }

    return (
        <div className={`tf-risk-${(TakeFiveRiskEnum[riskLevel]).toLowerCase()}`}>
            <FiberManualRecord />
            <div>{TakeFiveRiskEnum[riskLevel]}</div>
        </div>
    );
}

const PageSizeDropdown = (props: IPageSizeDropdownProps) => {
    return (
        <span className="psl-list-container">
            <div className="psl-list">
                {props.sizes.map(s =>
                    <div key={s}
                        className={`psl-item${s === props.pageSize ? " selected" : ""}`}
                        onClick={() => props.setPageSize(s)}>{s} items</div>
                )}
            </div>
        </span>
    );
}

interface IPageSizeDropdownProps {
    pageSize: number,
    setPageSize: (pageSize: number) => void,
    defaultExpanded?: boolean,
    sizes: number[]
}

const PageSizeWithPicker = (props: IPageSizeDropdownProps) => {
    return (
        <Toggle label={props.pageSize + " items"} collapseOnChangeOf={props.pageSize}>
            <PageSizeDropdown {...props} />
        </Toggle>
    );
};

interface ResultCountProps {
    viewCount: number | undefined,
    resultCount: number | undefined,
    pageSize: number,
    setPageSize: any,
    link?: string,
    visible?: boolean
}

const ResultCountContainer = (props: ResultCountProps) => {

    if (!props.visible) {
        return (<></>);
    }

    return (
        <div className="fa-list-resultcount float-right">
            {props.link &&
                <Link to={props.link} className="fa-list-resultcount-link">
                    <span className="fa-list-resultcount-text">{props.viewCount} of {props.resultCount} results</span>
                    <LinkIcon />
                </Link>
            }
            {!props.link &&
                <div className="fa-list-resultcount-itemsPerPage">
                    <span className="picker">
                        <PageSizeWithPicker
                            pageSize={props.pageSize}
                            setPageSize={props.setPageSize}
                            sizes={[10, 25, 50, 100]} />
                    </span>
                    <span> per page of {props.resultCount} results</span>
                </div>
            }
        </div>
    );
}

interface FooterProps {
    pageNumber: number,
    pageSize: number,
    totalResults: number,
    moveToPage: any,
    visible?: boolean
}

const FooterContainer = (props: FooterProps) => {

    if (!props.visible) {
        return (<></>);
    }

    return (
        <div className="tf-list-footer">
            <div className="page-list">
                <PageList {...props} />
            </div>
        </div>
    );
}